/**
 * The Formatting page (decision 78; part 15f log): beside the book's preview, the templates as a shelf of cards, and the
 * chapter heading's settings. It takes the text's place in the workspace, so the preview, its worker and its caches
 * stay as they are, and moving between Writing and Formatting costs no layout.
 *
 * A change shows at once from memory; the write waits for the control to settle (table TL, TL21).
 */
import { createEffect, createMemo, createSignal, mergeProps, onCleanup } from 'solid-js';
import { imageDestination } from '@cockatrice/core';
import { DEFAULT_THEME, FONT_FAMILIES, FONT_IDS, headingLooks, MAX_FADE, THEME_FIELDS } from '@cockatrice/layout';
import type { ChapterNumbering, FontId, HeadingLook, Theme } from '@cockatrice/layout';
import type { ChapterEntry } from '@cockatrice/storage';
import { fileUrls, urlOf } from '../preview/assets.ts';
import { FONT_URLS } from '../preview/font-urls.ts';
import { cleanTemplateName } from '../shell/templates.ts';
import type { TemplateView } from '../shell/templates.ts';
import { focusOn } from './announce.ts';
import { cleanTitle } from '../shell/workspace.ts';
import type { BookSummary } from '../shell/workspace.ts';
import { photoError } from './book-window.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import ConfirmDialog from '../ui/ConfirmDialog.ts';
import IconButton from '../ui/IconButton.ts';
import Menu from '../ui/Menu.ts';
import type { MenuTriggerProps } from '../ui/Menu.ts';
import Modal from '../ui/Modal.ts';
import NewBookTile from '../ui/NewBookTile.ts';
import Section from '../ui/Section.ts';
import SegmentedControl from '../ui/SegmentedControl.ts';
import Select from '../ui/Select.ts';
import Slider from '../ui/Slider.ts';
import TextField from '../ui/TextField.ts';
import Toggle from '../ui/Toggle.ts';
import { component } from '../ui/solid.ts';
import { each, el, indexed, when, whenSome } from '../ui/dom.ts';
import type { Child, On } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

/** TL21: how long a control rests before its change is written. */
export const THEME_SETTLE_MS = 300;

/** The heading's three lines as the theme's fields name them. */
type Line = 'Number' | 'Title' | 'Subtitle';

/** Each line's fields in the theme. */
const LINE_FIELDS = {
  Number: { look: 'number', font: 'chapterNumberFont', align: 'chapterNumberAlign', bold: 'chapterNumberBold', italic: 'chapterNumberItalic', style: 'chapterNumberStyle', size: 'chapterNumberSize', fade: 'chapterNumberFade' },
  Title: { look: 'title', font: 'chapterTitleFont', align: 'chapterTitleAlign', bold: 'chapterTitleBold', italic: 'chapterTitleItalic', style: 'chapterTitleStyle', size: 'chapterTitleSize', fade: 'chapterTitleFade' },
  Subtitle: { look: 'subtitle', font: 'chapterSubtitleFont', align: 'chapterSubtitleAlign', bold: 'chapterSubtitleBold', italic: 'chapterSubtitleItalic', style: 'chapterSubtitleStyle', size: 'chapterSubtitleSize', fade: 'chapterSubtitleFade' },
} as const;

/** A font's sample family: the font's own face, loaded the first time something shows it (decision 31: lazily). */
const sampled = new Map<FontId, Promise<void>>();

/** The CSS family a font's sample shows in, with a serif while it loads. */
export function sampleFamily(font: FontId): string {
  return `"sample-${font}", serif`;
}

/** Starts loading `font`'s regular face for samples, once; a failed load leaves the serif in its place. */
export function loadSample(font: FontId, fonts: Pick<FontFaceSet, 'add'> = document.fonts): void {
  if (sampled.has(font)) return;
  const face = new FontFace(`sample-${font}`, `url(${urlOf(FONT_URLS, `${font}-regular.ttf`)})`);
  sampled.set(font, face.load().then(
    (loaded) => {
      fonts.add(loaded);
    },
    () => undefined,
  ));
}

/** Calls `use` with `value` as one of `values`: a control's own options, typed. */
function known<T extends string>(values: readonly T[], value: string, use: (known: T) => void): void {
  for (const each of values) if (each === value) use(each);
}

/** Decision 78: favourite fonts first, each kept in the curated order. */
export function orderedFonts(favourites: readonly FontId[]): readonly FontId[] {
  return [...FONT_IDS.filter((font) => favourites.includes(font)), ...FONT_IDS.filter((font) => !favourites.includes(font))];
}

/** Decision 78: the font window: each font in its own face, a star beside it, the favourites first. */
function FontPicker(shell: Shell, label: () => string, value: () => FontId, choose: (font: FontId) => void, close: () => void): Child {
  const { t, workspace } = shell;
  const favourites = (): readonly FontId[] => workspace.settings().favouriteFonts;
  for (const font of FONT_IDS) loadSample(font);
  const star = (font: FontId): void => {
    const now = favourites();
    void workspace.updateSettings({ favouriteFonts: now.includes(font) ? now.filter((each) => each !== font) : [...now, font] });
  };
  return component(Modal, {
    open: true,
    size: 'sm',
    get title() {
      return label();
    },
    onClose: close,
    children: el(
      'ul',
      { class: 'font-list' },
      // Kept by place, so a press keeps the focus; a star moves the font, and its star takes the focus with it.
      indexed(
        () => orderedFonts(favourites()),
        (font) =>
          el(
            'li',
            { class: 'font-row' },
            el(
              'button',
              {
                type: 'button',
                class: 'font-choice',
                'aria-pressed': () => (value() === font() ? 'true' : 'false'),
                style: () => ({ 'font-family': sampleFamily(font()) }),
                onClick: () => {
                  choose(font());
                  close();
                },
              },
              () => FONT_FAMILIES[font()],
            ),
            component(Toggle, {
              variant: 'button',
              iconOnly: true,
              icon: 'star',
              class: 'font-star',
              get 'data-font'() {
                return font();
              },
              get label() {
                return t('format.fontStar', { font: FONT_FAMILIES[font()] });
              },
              get pressed() {
                return favourites().includes(font());
              },
              onChange: () => {
                const starred = font();
                star(starred);
                queueMicrotask(() => {
                  focusOn(shell, `.font-star[data-font="${starred}"]`);
                });
              },
            }),
          ),
      ),
    ),
  });
}

/** One heading line on a card, in the line's look. */
function CardLine(look: () => HeadingLook, text: string, shown: () => boolean): Child {
  return when(shown, () =>
    el(
      'span',
      {
        class: 'template-line',
        style: () => {
          const { font, align, bold, italic, style, fade, size } = look();
          return {
            'font-family': sampleFamily(font),
            'text-align': align,
            'font-weight': bold ? '700' : '400',
            'font-style': italic ? 'italic' : 'normal',
            'font-variant': style === 'small-caps' ? 'small-caps' : 'normal',
            'text-transform': style === 'uppercase' ? 'uppercase' : 'none',
            opacity: String((100 - fade) / 100),
            'font-size': `${String(Math.min(size / 4, 18))}px`,
          };
        },
      },
      text,
    ),
  );
}

/** What a card asks of the shelf. */
interface CardActions {
  readonly chosen: () => string | null;
  readonly remove: (template: TemplateView) => void;
  readonly rename: (template: TemplateView) => void;
}

/** One template's card: a small chapter opening in its look, its star at the top, its menu at the foot; it follows the template at its place. */
function TemplateCard(shell: Shell, template: () => TemplateView, actions: CardActions): Child {
  const { t, workspace } = shell;
  const looks = createMemo(() => headingLooks(template().theme));
  createEffect(() => {
    loadSample(looks().title.font);
  });
  const heading = (): boolean => template().theme.chapterHeading;
  return el(
    'li',
    { class: 'template-card', 'data-template': () => template().id },
    el(
      'button',
      {
        type: 'button',
        class: 'template-page',
        'aria-pressed': () => (actions.chosen() === template().id ? 'true' : 'false'),
        'aria-label': () => t('format.use', { name: template().name }),
        onClick: () => {
          void workspace.chooseTemplate(template().id);
        },
      },
      CardLine(() => looks().number, t('format.sampleNumber'), () => heading() && template().theme.chapterNumbering !== 'none'),
      CardLine(() => looks().title, t('format.sampleTitle'), heading),
      el('span', { class: 'template-text', 'aria-hidden': 'true' }),
    ),
    component(Toggle, {
      variant: 'button',
      iconOnly: true,
      icon: 'star',
      class: 'template-star',
      get label() {
        return t('format.star', { name: template().name });
      },
      get pressed() {
        return template().favourite;
      },
      onChange: () => {
        const { id } = template();
        // The card moves; its star takes the focus with it.
        void workspace.starTemplate(id).then(() => {
          focusOn(shell, `[data-template="${id}"] .template-star`);
        });
      },
    }),
    el(
      'div',
      { class: 'template-foot' },
      el('span', { class: 'template-name' }, () => template().name),
      component(Menu, {
        label: t('format.more', { name: template().name }),
        class: 'template-more',
        items: [
          { label: t('format.duplicate'), onSelect: () => void workspace.duplicateTemplate(template().id) },
          ...(template().builtIn
            ? []
            : [
                { label: t('format.rename'), onSelect: () => { actions.rename(template()); } },
                { label: t('format.delete'), danger: true, onSelect: () => { actions.remove(template()); } },
              ]),
        ],
        trigger: (props: MenuTriggerProps) =>
          component(
            IconButton,
            mergeProps(props, {
              icon: 'more' as const,
              get label() {
                return t('format.more', { name: template().name });
              },
            }),
          ),
      }),
    ),
  );
}

/** TL12: a template's new name. */
export function RenameTemplate(shell: Shell, template: TemplateView, close: () => void): Child {
  const { t, workspace } = shell;
  const [name, setName] = createSignal(template.name);
  const save = (): void => {
    if (cleanTemplateName(name()) !== '') void workspace.renameTemplate(template.id, name());
    close();
  };
  return component(Modal, {
    open: true,
    size: 'sm',
    title: t('format.renameTitle', { name: template.name }),
    onClose: close,
    footer: [
      component(Button, { variant: 'ghost', onClick: close, children: t('format.cancel') }),
      component(Button, { variant: 'primary', class: 'template-rename-save', onClick: save, children: t('format.rename') }),
    ],
    children: el(
      'form',
      {
        onSubmit: (event: On<SubmitEvent, HTMLFormElement>) => {
          event.preventDefault();
          save();
        },
      },
      component(TextField, {
        label: t('format.name'),
        class: 'template-rename',
        get value() {
          return name();
        },
        onChange: (value: string) => {
          setName(value);
        },
      }),
    ),
  });
}

/** Decision 78: the templates, a New template tile first. */
function TemplateShelf(shell: Shell): Child {
  const { t, workspace } = shell;
  const [asking, setAsking] = createSignal<TemplateView | null>(null);
  const [renaming, setRenaming] = createSignal<TemplateView | null>(null);
  // Read once for all the cards, so a setting's change costs no card anything (TL21).
  const templates = createMemo(() => workspace.templates());
  const chosen = createMemo(() => workspace.bookTemplate());
  const ids = createMemo(() => templates().map((template) => template.id));
  return [
    el(
      'ul',
      { class: 'template-shelf', 'aria-label': t('format.templates') },
      el(
        'li',
        { class: 'template-card' },
        component(NewBookTile, {
          label: t('format.new'),
          hint: '',
          class: 'template-new',
          onClick: () => {
            void workspace.addTemplate(t('format.newName'));
          },
        }),
      ),
      // Keyed by ID: a card, and the menu made with it, are one template's for good; a press keeps the focus.
      each(ids, (id) => {
        // A deleted template's card goes with the list; until it does it keeps what it showed.
        const template = createMemo<TemplateView>((last) => templates().reduce((found, view) => (view.id === id ? view : found), last), { id, name: '', theme: DEFAULT_THEME, builtIn: false, favourite: false, createdAt: 0 });
        return TemplateCard(shell, template, { chosen, remove: setAsking, rename: setRenaming });
      }),
    ),
    whenSome(renaming, (template) => RenameTemplate(shell, template(), () => setRenaming(null))),
    whenSome(asking, (template) => {
      const books = workspace.templateBooks(template().id);
      return component(ConfirmDialog, {
        open: true,
        tone: 'danger',
        title: t('format.deleteTitle', { name: template().name }),
        message: books.length === 0 ? t('format.deleteUnused') : t('format.deleteUsed', { books: books.join(', ') }),
        confirmLabel: t('format.delete'),
        onClose: () => setAsking(null),
        onConfirm: () => {
          void workspace.removeTemplate(template().id);
          setAsking(null);
        },
      });
    }),
  ];
}

/** The settings of one heading line, the same for each (decision 78). */
function LineSettings(shell: Shell, line: Line, theme: () => Theme, change: (changes: Partial<Theme>) => void, extra: Child = null): Child {
  const { t } = shell;
  const names = LINE_FIELDS[line];
  const look = (): HeadingLook => headingLooks(theme())[names.look];
  const [picking, setPicking] = createSignal(false);
  const font = (): FontId => look().font;
  const range = THEME_FIELDS[names.size];
  const fontLabel = (): string => t(`format.font${line}`);
  return el(
    'fieldset',
    { class: 'format-line' },
    el('legend', {}, t(`format.line${line}`)),
    extra,
    el(
      'div',
      { class: 'format-row' },
      el('span', { class: 'format-label' }, t('format.font')),
      component(Button, {
        variant: 'secondary',
        size: 'sm',
        class: 'font-open',
        'aria-haspopup': 'dialog',
        get style() {
          return { 'font-family': sampleFamily(font()) };
        },
        onClick: () => {
          loadSample(font());
          setPicking(true);
        },
        get children() {
          return FONT_FAMILIES[font()];
        },
      }),
    ),
    when(picking, () => FontPicker(shell, fontLabel, font, (chosen) => { change({ [names.font]: chosen }); }, () => setPicking(false))),
    component(SegmentedControl, {
      label: t('format.align'),
      size: 'sm',
      options: [
        { value: 'left', label: t('format.left') },
        { value: 'center', label: t('format.center') },
        { value: 'right', label: t('format.right') },
      ],
      get value() {
        return look().align;
      },
      onChange: (align: string) => {
        change({ [names.align]: align });
      },
    }),
    el(
      'div',
      { class: 'format-styles', role: 'group', 'aria-label': t('format.style') },
      component(Toggle, { variant: 'button', size: 'sm', label: t('format.bold'), get pressed() { return look().bold; }, onChange: (on: boolean) => { change({ [names.bold]: on }); } }),
      component(Toggle, { variant: 'button', size: 'sm', label: t('format.italic'), get pressed() { return look().italic; }, onChange: (on: boolean) => { change({ [names.italic]: on }); } }),
      component(Toggle, {
        variant: 'button',
        size: 'sm',
        label: t('format.caps'),
        get pressed() {
          return look().style === 'uppercase';
        },
        onChange: (on: boolean) => {
          change({ [names.style]: on ? 'uppercase' : 'normal' });
        },
      }),
    ),
    component(Slider, {
      label: t('format.size'),
      min: range.min,
      max: range.max,
      get value() {
        return look().size;
      },
      format: (size: number) => t('format.points', { size: size / 2 }),
      onChange: (size: number) => {
        change({ [names.size]: size });
      },
    }),
    component(Slider, {
      label: t('format.fade'),
      help: t('format.fadeHelp'),
      min: 0,
      max: MAX_FADE,
      step: 5,
      get value() {
        return look().fade;
      },
      unit: ' %',
      onChange: (fade: number) => {
        change({ [names.fade]: fade });
      },
    }),
  );
}

/** Decision 78: the space above a line, moved by up and down controls within limits. */
function Gap(shell: Shell, label: string, value: () => number, set: (gap: number) => void, max: number): Child {
  const { t } = shell;
  return el(
    'div',
    { class: 'format-row format-gap' },
    el('span', { class: 'format-label' }, label),
    component(IconButton, { icon: 'chevronUp', label: t('format.gapLess', { label }), get disabled() { return value() <= 0; }, onClick: () => { set(value() - 1); } }),
    el('output', {}, () => t('format.ems', { size: value() / 10 })),
    component(IconButton, { icon: 'chevronDown', label: t('format.gapMore', { label }), get disabled() { return value() >= max; }, onClick: () => { set(value() + 1); } }),
  );
}

/** The ornaments shipped with the app: each one's file, fetched only when the Gallery opens. */
const ORNAMENT_URLS = fileUrls(import.meta.glob('../images/ornaments/*.png', { eager: true, query: '?url', import: 'default' }));

/** Each ornament's file and its name for a screen reader. */
const ORNAMENT_NAMES = [
  ['flourish', 'format.ornament.flourish'],
  ['frame-corners', 'format.ornament.frame-corners'],
  ['leaf-spray', 'format.ornament.leaf-spray'],
  ['rule-diamond', 'format.ornament.rule-diamond'],
  ['rule-dots', 'format.ornament.rule-dots'],
  ['star-burst', 'format.ornament.star-burst'],
] as const;

type OrnamentLabel = (typeof ORNAMENT_NAMES)[number][1];

/** A picture the Gallery offers: an ornament's file, or an image the writer stored in a book. */
type GalleryItem = { readonly kind: 'ornament'; readonly url: string; readonly label: OrnamentLabel } | { readonly kind: 'stored'; readonly bookId: string; readonly destination: string; readonly url: string };

/** Decision 78: the Gallery: the writer's images, and the ornaments we ship. */
function Gallery(shell: Shell, bookId: string, choose: (destination: string) => void, failed: (reason: string) => void, close: () => void): Child {
  const { t, workspace } = shell;
  const [stored, setStored] = createSignal<readonly GalleryItem[]>([]);
  const urls: string[] = [];
  onCleanup(() => {
    for (const url of urls) URL.revokeObjectURL(url);
  });
  void (async () => {
    const items: GalleryItem[] = [];
    for (const image of await workspace.galleryImages()) {
      const file = await workspace.imageFile(image.bookId, image.destination);
      if (file === null) continue;
      const url = URL.createObjectURL(file);
      urls.push(url);
      items.push({ kind: 'stored', ...image, url });
    }
    setStored(items);
  })();
  const ornaments: readonly GalleryItem[] = ORNAMENT_NAMES.map(([file, label]) => ({ kind: 'ornament', url: urlOf(ORNAMENT_URLS, `${file}.png`), label }));
  const pick = async (item: GalleryItem): Promise<void> => {
    try {
      if (item.kind === 'stored') {
        if (await workspace.bringImage(item.bookId, item.destination)) choose(item.destination);
        else failed(t('cover.refused.failed'));
      } else {
        const made = await shell.importPicture(bookId, await (await fetch(item.url)).blob());
        if (made.kind === 'added' || made.kind === 'existing') choose(imageDestination(made.reference));
        else failed(photoError(shell, made));
      }
    } catch {
      // An ornament's file that couldn't be fetched (offline before it was cached).
      failed(t('cover.refused.failed'));
    }
    close();
  };
  const tile = (item: GalleryItem): Child =>
    el(
      'li',
      {},
      el(
        'button',
        {
          type: 'button',
          class: 'gallery-tile',
          'aria-label': t(item.kind === 'ornament' ? item.label : 'format.yourImage'),
          onClick: () => {
            void pick(item);
          },
        },
        el('img', { src: item.url, alt: '' }),
      ),
    );
  return component(Modal, {
    open: true,
    size: 'lg',
    title: t('format.gallery'),
    onClose: close,
    children: [
      el('h3', {}, t('format.yourImages')),
      when(
        () => stored().length > 0,
        () => el('ul', { class: 'gallery-grid' }, () => stored().map(tile)),
        () => el('p', {}, t('format.noImages')),
      ),
      el('h3', {}, t('format.ourImages')),
      el('ul', { class: 'gallery-grid' }, ornaments.map(tile)),
    ],
  });
}

/** Decision 78: the chapter image's settings: where it comes from, then, once one shows, how it sits. */
function ImageSettings(shell: Shell, book: () => BookSummary, open: () => ChapterEntry | null, change: (changes: Partial<Theme>) => void): Child {
  const { t, workspace } = shell;
  const theme = (): Theme => book().theme;
  const [gallery, setGallery] = createSignal(false);
  const [problem, setProblem] = createSignal<string | null>(null);
  const shown = (): string => (theme().chapterImagePerChapter ? (open()?.image ?? '') : theme().chapterImage);
  const set = (destination: string): void => {
    setProblem(null);
    const chapter = open();
    if (theme().chapterImagePerChapter && chapter !== null) void workspace.setChapterImage(chapter.id, destination);
    else change({ chapterImage: destination });
  };
  // Its ref sets the real one, before any press.
  let picker = document.createElement('input');
  return el(
    'fieldset',
    { class: 'format-line format-image' },
    el('legend', {}, t('format.image')),
    component(Checkbox, {
      label: t('format.imagePerChapter'),
      class: 'image-per-chapter',
      get checked() {
        return theme().chapterImagePerChapter;
      },
      onChange: (on: boolean) => {
        change({ chapterImagePerChapter: on });
      },
    }),
    el(
      'div',
      { class: 'format-row' },
      component(Button, {
        variant: 'secondary',
        size: 'sm',
        class: 'image-upload',
        onClick: () => {
          picker.click();
        },
        children: t('format.upload'),
      }),
      component(Button, {
        variant: 'secondary',
        size: 'sm',
        class: 'image-gallery',
        'aria-haspopup': 'dialog',
        onClick: () => setGallery(true),
        children: t('format.gallery'),
      }),
      el('input', {
        type: 'file',
        accept: 'image/jpeg,image/png,image/webp',
        hidden: true,
        tabIndex: -1,
        'aria-hidden': 'true',
        ref: (input: HTMLInputElement) => {
          picker = input;
        },
        onChange: (event: On<Event, HTMLInputElement>) => {
          const [file] = Array.from(event.currentTarget.files ?? []);
          event.currentTarget.value = '';
          if (file === undefined) return;
          void shell.importPicture(book().id, file).then((made) => {
            if (made.kind === 'added' || made.kind === 'existing') set(imageDestination(made.reference));
            else setProblem(photoError(shell, made));
          });
        },
      }),
    ),
    whenSome(problem, (reason) => el('p', { class: 'format-problem', role: 'alert' }, reason)),
    when(gallery, () => Gallery(shell, book().id, set, setProblem, () => setGallery(false))),
    // Decision 78: the rest shows once an image does.
    when(
      () => shown() !== '',
      () => [
        component(Button, { variant: 'ghost', size: 'sm', class: 'image-remove', onClick: () => { set(''); }, children: t('format.imageRemove') }),
        component(SegmentedControl, {
          label: t('format.imagePlace'),
          size: 'sm',
          options: [
            { value: 'above', label: t('format.imageAbove') },
            { value: 'between', label: t('format.imageBetween') },
            { value: 'below', label: t('format.imageBelow') },
            { value: 'behind', label: t('format.imageBehind') },
          ],
          get value() {
            return theme().chapterImagePlace;
          },
          onChange: (place: string) => {
            known(THEME_FIELDS.chapterImagePlace.values, place, (chapterImagePlace) => {
              change({ chapterImagePlace });
            });
          },
        }),
        component(SegmentedControl, {
          label: t('format.align'),
          size: 'sm',
          options: [
            { value: 'left', label: t('format.left') },
            { value: 'center', label: t('format.center') },
            { value: 'right', label: t('format.right') },
          ],
          get value() {
            return theme().chapterImageAlign;
          },
          onChange: (align: string) => {
            known(THEME_FIELDS.chapterImageAlign.values, align, (chapterImageAlign) => {
              change({ chapterImageAlign });
            });
          },
        }),
        component(Slider, { label: t('format.size'), min: 10, max: 100, step: 5, unit: ' %', get value() { return theme().chapterImageSize; }, onChange: (size: number) => { change({ chapterImageSize: size }); } }),
        component(Slider, { label: t('format.fade'), min: 0, max: 90, step: 5, unit: ' %', get value() { return theme().chapterImageFade; }, onChange: (fade: number) => { change({ chapterImageFade: fade }); } }),
        // T‴7: only an image at the page's top can run off its edges.
        when(
          () => theme().chapterImagePlace === 'above' || theme().chapterImagePlace === 'behind',
          () =>
            component(Checkbox, {
              label: t('format.bleed'),
              description: t('format.bleedHelp'),
              class: 'image-bleed',
              get checked() {
                return theme().chapterImageBleed;
              },
              onChange: (on: boolean) => {
                change({ chapterImageBleed: on });
              },
            }),
        ),
      ],
    ),
  );
}

/** Decision 78: the chapter heading's settings. */
function HeadingSettings(shell: Shell, change: (changes: Partial<Theme>) => void): Child {
  const { t, workspace } = shell;
  const book = createMemo(() => workspace.book());
  const open = createMemo(() => (workspace.listing()?.chapters ?? []).find((entry) => entry.id === workspace.chapterId()) ?? null);
  const numbers: readonly ChapterNumbering[] = ['arabic', 'word', 'chapter-word', 'chapter-arabic', 'roman', 'none'];
  return component(Section, {
    title: t('format.heading'),
    level: 2,
    children: whenSome(book, (shown) => {
      const current = (): Theme => shown().theme;
      return [

      component(Checkbox, {
        label: t('format.headingOn'),
        class: 'heading-on',
        get checked() {
          return current().chapterHeading;
        },
        onChange: (on: boolean) => {
          change({ chapterHeading: on });
        },
      }),
      when(
        () => current().chapterHeading,
        () => [
          LineSettings(
            shell,
            'Number',
            current,
            change,
            component(Select, {
              label: t('format.display'),
              options: numbers.map((value) => ({ value, label: t(`format.display.${value}`) })),
              get value() {
                return current().chapterNumbering;
              },
              onChange: (value: string) => {
                known(numbers, value, (chapterNumbering) => {
                  change({ chapterNumbering });
                });
              },
            }),
          ),
          LineSettings(shell, 'Title', current, change, Gap(shell, t('format.titleGap'), () => current().chapterTitleGap, (gap) => { change({ chapterTitleGap: gap }); }, THEME_FIELDS.chapterTitleGap.max)),
          LineSettings(
            shell,
            'Subtitle',
            current,
            change,
            [
              whenSome(open, (entry) =>
                component(TextField, {
                  label: t('format.subtitleText'),
                  help: t('format.subtitleHelp'),
                  get value() {
                    return entry().subtitle;
                  },
                  onCommit: (value: string) => {
                    void workspace.setSubtitle(entry().id, cleanTitle(value));
                  },
                }),
              ),
              Gap(shell, t('format.subtitleGap'), () => current().chapterSubtitleGap, (gap) => { change({ chapterSubtitleGap: gap }); }, THEME_FIELDS.chapterSubtitleGap.max),
            ],
          ),
          ImageSettings(shell, shown, open, change),
        ],
      ),
      ];
    }),
  });
}

/** The Formatting page's column: it takes the text's place beside the preview. */
export function FormattingPanel(shell: Shell, notes: Child): Child {
  const { t, workspace } = shell;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const save = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    void workspace.saveTheme();
  };
  const change = (changes: Partial<Theme>): void => {
    workspace.previewTheme(changes);
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(save, THEME_SETTLE_MS);
  };
  // TL21: nothing shown stays unwritten when the writer leaves the page or the tab goes away.
  window.addEventListener('pagehide', save);
  createEffect(() => {
    if (!shell.pageVisible()) save();
  });
  onCleanup(() => {
    window.removeEventListener('pagehide', save);
    save();
  });
  return el(
    'main',
    { class: 'format-pane', id: 'formatting', tabIndex: -1 },
    // What the writer must see here too: the text's column, where these show, is hidden.
    notes,
    el('h1', { id: 'format-title', tabIndex: -1 }, t('format.title')),
    // TL4 and a read-only account: nothing here can change, and every control says so.
    el('fieldset', { class: 'format-controls', disabled: () => !workspace.writable() }, TemplateShelf(shell), HeadingSettings(shell, change)),
  );
}
